/**
 * @engine/aid - Asset Index Database
 * 
 * FineIndex provides Unity-like AssetDatabase functionality:
 * - Persistent GUIDs for assets
 * - Importer settings per asset
 * - Fast indexed queries by type
 * - Change detection and sync with VFS
 * 
 * Content types and importers are registered externally.
 * 
 * @module @engine/aid
 */

// Main API
export { FineIndex, type FineIndexDBFactory } from './FineIndex';

// TOML .meta sidecars (git source of truth for asset identity)
export {
  META_VERSION,
  META_EXT,
  type AssetMeta,
  type MetaFileVfs,
  isMetaPath,
  metaPathFor,
  assetPathForMeta,
  emitCanonicalToml,
  metaFromEntry,
  parseMeta,
  readMeta,
  writeMeta,
  deleteMeta,
  moveMeta,
} from './MetaFile';

// Database layers
export { FineIndexDB, deleteFineIndexDB } from './FineIndexDB';
export { FineIndexSQLite, isNodeSQLiteSupported } from './FineIndexSQLite';

// Import settings
export {
  // Texture settings
  type TextureFormat,
  type CompressionQuality,
  type MaxTextureSize,
  type AlphaMode,
  type FilterMode,
  type WrapMode,
  type ChannelFormat,
  type SwizzleChannel,
  type SwizzleMask,
  type SpriteMode,
  type TextureImportSettings,
  type CompiledTextureInfo,
  DEFAULT_TEXTURE_SETTINGS,
  
  // Audio settings (future)
  type AudioFormat,
  type AudioImportSettings,
  DEFAULT_AUDIO_SETTINGS,
  
  // Script settings (per-script)
  type ScriptImportSettings,
  DEFAULT_SCRIPT_SETTINGS,
} from './importSettings';

// Types
export {
  // Asset entry
  type AssetEntry,
  type ProducedAsset,
  
  // Registration types
  type ContentTypeDefinition,
  type ImporterDefinition,
  type ImporterParamsSchema,
  
  // Importer system
  type ImportContext,
  type ImportResult,
  type ImportArtifact,
  type ImporterCallback,
  
  // Query types
  type AssetQuery,
  type AssetQueryResult,
  
  // Change tracking
  type ChangeType,
  type AssetChange,
  type RefreshResult,
  
  // Statistics
  type IndexStats,
  
  // Progress
  type ProgressInfo,
  type ProgressCallback,
  
  // Database provider interface
  type IFineIndexDB,
  
  // Helpers
  generateGUID,
  deriveProducedGuid,
  hashContent,
} from './types';
